/* import __COLOCATED_TEMPLATE__ from './inbox-title.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type Session from 'embercom/services/session';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency-decorators';
import ajax from 'embercom/lib/ajax';
import { objectTypes, states } from 'embercom/models/data/matching-system/matching-constants';
import { taskFor } from 'ember-concurrency-ts';
import moment from 'moment-timezone';
import type Router from '@ember/routing/router-service';
import { SEARCH_URL } from 'embercom/lib/content-service/constants';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import type InboxApi from 'embercom/services/inbox-api';
import { InboxType } from 'embercom/models/data/inbox/inbox-types';
import type Inbox from 'embercom/objects/inbox/inboxes/inbox';
import type InboxState from 'embercom/services/inbox-state';
import { AsyncData } from 'embercom/resources/utils/async-data';
import { use } from 'ember-resources/util/function-resource';
import View from 'embercom/objects/inbox/inboxes/view';

interface IntercomEventService {
  trackAnalyticsEvent(event: Record<string, unknown>): void;
}

interface Signature {
  Element: HTMLDivElement;
  Args: {
    titleTranslationKey?: string | null;
    titleUntranslated?: string | null;
    showSearch?: boolean;
    onToggleSearch?: (show: boolean) => void;
  };
}

interface FilterOptionItem {
  id: string;
  type: InboxType;
  name: string;
  icon: string;
  isSelected: boolean;
  text: string;
  value: string;
  component: string;
}

interface FilterOption {
  items: FilterOptionItem[];
}

const STANDALONE_FILTER_VIEWS = [
  'ai_agent_all_conversations',
  'ai_agent_routed',
  'ai_agent_resolved',
  'ai_agent_abandoned',
];

export default class InboxTitle extends Component<Signature> {
  @service declare session: Session;
  @tracked liveWorkflows: any = null;
  @service declare router: Router;
  @service declare intl: IntlService;
  @service declare inboxApi: InboxApi;
  @service declare inboxState: InboxState;
  @service declare intercomEventService: IntercomEventService;

  @use inboxesResource = AsyncData<Inbox[]>(async () => {
    if (!this.session.workspace.hasLightStandaloneInbox) {
      return [];
    }

    try {
      let result = await this.inboxApi.listInboxes({
        pinned: true,
        types: [InboxType.All, InboxType.View],
        includeFolders: true,
        includeCounts: true,
      });

      return result.inboxes || [];
    } catch (error) {
      console.error('Error loading inboxes:', error);
      return [];
    }
  });

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    taskFor(this.getLiveWorkflows).perform();
  }

  @task({ drop: true })
  *getLiveWorkflows() {
    try {
      // @ts-ignore
      let liveWorkflowsSearch: any = yield this.contentSearch({
        object_types: [
          objectTypes.customBot,
          objectTypes.inboundCustomBot,
          objectTypes.buttonCustomBot,
          objectTypes.triggerableCustomBot,
        ],
        states: [states.live],
        match_behaviors: [],
        app_id: this.app?.id,
        per_page: 2,
      });
      this.liveWorkflows = liveWorkflowsSearch;
    } catch (e) {
      console.error(e);
    }
  }

  async contentSearch(searchParams: any) {
    let response: any = await ajax({
      url: SEARCH_URL,
      type: 'GET',
      data: searchParams,
    }).catch(() => {});

    return {
      totalCount: response.total_count,
    };
  }

  get hasNoLiveWorkflows() {
    return this.liveWorkflows?.totalCount < 1;
  }

  get isInUnassignedRoute() {
    return this.router.currentURL?.includes('unassigned');
  }

  get daysSinceWorkspaceCreated() {
    let dateCreated = this.session.workspace?.createdAt;
    let today = moment();
    let diff = today.diff(dateCreated, 'days');
    return diff < 30;
  }

  get app() {
    return this.session.workspace;
  }

  get isAnyChannelInstalled() {
    return this.app?.isAnyChannelInstalled;
  }

  get hasAccessToWorkflows() {
    return this.app?.hasAccessToWorkflows;
  }

  get listOfInboxes(): Inbox[] {
    return this.inboxesResource.value || [];
  }

  get selectedFilterId(): string | undefined {
    let activeInbox = this.inboxState.activeInbox;
    if (!activeInbox) {
      return undefined;
    }

    let isInFilteredList = this.listOfInboxes.some((inbox) => inbox.id === activeInbox.id);
    return isInFilteredList ? activeInbox.id : undefined;
  }

  get selectedFilter(): Inbox | undefined {
    let inboxes = this.listOfInboxes;
    if (this.selectedFilterId) {
      return inboxes.find((inbox) => inbox.id === this.selectedFilterId);
    }
    return inboxes.length > 0 ? inboxes[0] : undefined;
  }

  get filterOptions(): FilterOption[] {
    let inboxes = this.listOfInboxes;
    if (inboxes.length === 0) {
      return [];
    }

    let filteredInboxes = inboxes.filter((inbox, index, self) => {
      let isAllowedType = false;
      if (inbox instanceof View) {
        isAllowedType = STANDALONE_FILTER_VIEWS.includes(inbox.viewSummary?.systemType);
      } else if (inbox.type === InboxType.All) {
        isAllowedType = true;
      }
      if (!isAllowedType) {
        return false;
      }

      if (inbox instanceof View) {
        return (
          index ===
          self.findIndex(
            (i) => i instanceof View && i.viewSummary?.systemType === inbox.viewSummary?.systemType,
          )
        );
      } else {
        return index === self.findIndex((i) => i.id === inbox.id);
      }
    });

    let items: FilterOptionItem[] = filteredInboxes.map((inbox: Inbox) => ({
      id: inbox.id,
      type: inbox.type,
      name: this.filterDropdownName(inbox),
      text: this.filterDropdownName(inbox),
      value: inbox.id,
      icon: this.getInboxIcon(inbox),
      isSelected: this.selectedFilter?.id === inbox.id,
      component: 'inbox2/left-nav/conversation-list/filters-item',
      tooltip: this.filterDropdownTooltip(inbox),
    }));

    items.sort((a, b) => {
      let getPriority = (type: InboxType) => {
        // type is an enum on the Inbox object rather than casting the subclass itself
        if (type === InboxType.View) {
          return 0;
        }
        if (type === InboxType.All) {
          return 2;
        }
        return 1;
      };

      return getPriority(a.type) - getPriority(b.type);
    });

    return [{ items }];
  }

  filterDropdownName(inbox: Inbox): string {
    if (inbox.type === InboxType.All) {
      return this.intl.t('inbox.left-nav.inbox-title.all-conversations');
    } else if (inbox instanceof View) {
      if (inbox.viewSummary?.systemType === 'ai_agent_all_conversations') {
        return this.intl.t('inbox.left-nav.inbox-title.involved');
      }
      if (
        inbox.viewSummary?.systemType === 'ai_agent_abandoned' &&
        this.session.workspace.isFeatureEnabled('new-label-for-abandoned')
      ) {
        return this.intl.t('inbox.left-nav.inbox-title.not_routed_or_resolved');
      }
      if (
        inbox.viewSummary?.systemType === 'ai_agent_routed' &&
        this.session.workspace.isFeatureEnabled('new-label-for-routed')
      ) {
        return this.intl.t('inbox.left-nav.inbox-title.escalated');
      }
    }
    return inbox.name;
  }

  filterDropdownTooltip(inbox: Inbox): string | undefined {
    if (inbox.type === InboxType.All) {
      return this.intl.t(`inbox.left-nav.inbox-title.tooltip.${inbox.type}`);
    } else if (inbox instanceof View) {
      if (
        inbox.viewSummary.systemType === 'ai_agent_abandoned' &&
        this.session.workspace.isFeatureEnabled('new-label-for-abandoned')
      ) {
        return this.intl.t('inbox.left-nav.inbox-title.tooltip.ai_agent_not_routed_or_resolved');
      }
      if (
        inbox.viewSummary?.systemType === 'ai_agent_routed' &&
        this.session.workspace.isFeatureEnabled('new-label-for-routed')
      ) {
        return this.intl.t('inbox.left-nav.inbox-title.tooltip.ai_agent_escalated');
      }

      return this.intl.t(`inbox.left-nav.inbox-title.tooltip.${inbox.viewSummary.systemType}`);
    }
    return undefined;
  }

  get isLoading(): boolean {
    return this.inboxesResource.isLoading;
  }

  get hasFilterOptions(): boolean {
    return this.filterOptions.length > 0 && this.filterOptions[0]?.items?.length > 0;
  }

  get isSingleConversationRoute(): boolean {
    return this.router.currentRouteName === 'inbox.workspace.inbox.conversation.conversation';
  }

  get getTitle(): string {
    if (this.session.workspace.hasLightStandaloneInbox) {
      if (this.selectedFilter) {
        return this.filterDropdownName(this.selectedFilter);
      }
      return this.intl.t('inbox.inbox-list.all-conversations');
    }
    if (this.args.titleTranslationKey) {
      return this.intl.t(this.args.titleTranslationKey);
    }
    return this.args.titleUntranslated || '';
  }

  @action
  onFilterSelect(selectedOption: any) {
    let selectedInbox = this.listOfInboxes.find((inbox) => inbox.id === selectedOption);
    if (selectedInbox) {
      this.inboxState.setInbox(selectedInbox);
    }
  }

  getInboxIcon(inbox: Inbox): string {
    // different icon for all conversations to reduce confusion
    if (inbox.type === InboxType.All) {
      return 'list' as string;
    }
    if (inbox.icon && 'name' in inbox.icon) {
      return inbox.icon.name as string;
    }
    return 'fin' as string;
  }

  @action
  toggleSearch() {
    let newShowSearchState = !this.args.showSearch;

    this.intercomEventService.trackAnalyticsEvent({
      action: newShowSearchState ? 'opened' : 'closed',
      object: 'search_in_view',
      section: 'inbox_conversation_list',
      view_id: this.inboxState.activeInbox?.id,
      view_type: this.inboxState.activeInbox?.type,
    });

    if (this.args.onToggleSearch) {
      this.args.onToggleSearch(newShowSearchState);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::LeftNav::ConversationList::InboxTitle': typeof InboxTitle;
    'inbox2/left-nav/conversation-list/inbox-title': typeof InboxTitle;
  }
}
